import "@/components/home/post.css";
import { memo } from "react";

export type PostCategory = "product" | "research" | "company";

/**
 * PostMeta — the blog's date line: the machine-readable date, a middot, and
 * the post's category set in the accent color.
 *
 * Four sites wrote it. The index wrote it twice, once inside PostCard's body
 * and once again in every directory row; the article wrote it twice more, in
 * its header and in each related-posts entry. Three of the four were
 * byte-identical, down to the spaces inside the aria-hidden separator span,
 * and only the strings differed — on the PostCard side those were already
 * props.
 *
 * The fourth had drifted by one class. The article prints this line inside
 * its column while the sticky rail beside it prints "Blog / research", so at
 * the rail's breakpoint the category would read twice. `.dw-post__meta-cat[data-variant="post"]`
 * hides the separator and the category there. That is placement, not a second
 * meta line, so it arrives as `variant="post"` and lands on those two spans
 * only (as `data-variant`) — never on the <time>, never on the <p>.
 *
 * The category is the closed set the index already typed. It is exported from
 * here as PostCategory so the index imports the set instead of restating it.
 *
 * Date lines without a category are a different shape and this component does
 * not own them: the Guides and Changelog strips print a bare label with no
 * <time> element at all, and the article's rail prints two links joined by a
 * slash.
 */
export const PostMeta = memo(function PostMeta({
  dateISO,
  dateLabel,
  category,
  variant,
}: {
  dateISO: string;
  dateLabel: string;
  category: PostCategory;
  variant?: "post";
}) {
  return (
    <p className="dw-text-caption dw-tabular-nums" data-tone="muted" data-component="PostMeta" data-location="src/components/home/post-meta.tsx">
      <time dateTime={dateISO}>{dateLabel}</time>
      <span aria-hidden className="dw-post__meta-cat" data-variant={variant}> · </span>
      <span className="dw-post__meta-cat" data-variant={variant} data-tone="primary">{category}</span>
    </p>
  );
});
